import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import {
  Check,
  ChevronRight,
  Wallet,
  Target,
  Share2,
  Compass,
  X,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
  actionUrl: string;
  optional?: boolean;
}

interface OnboardingProgress {
  completed: boolean;
  steps: OnboardingStep[];
  completedCount: number;
  totalSteps: number;
}

function getStepIcon(stepId: string) {
  switch (stepId) {
    case "create_wallet":
      return Wallet;
    case "set_goal":
      return Target;
    case "share_wallet":
      return Share2;
    case "explore_community":
      return Compass;
    default:
      return Wallet;
  }
}

export function OnboardingChecklist() {
  const { data: progress, isLoading } = useQuery<OnboardingProgress>({
    queryKey: ["/api/onboarding/progress"],
  });

  const dismissMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/onboarding/dismiss");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/onboarding/progress"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
    },
  });

  if (isLoading || !progress || progress.completed) {
    return null;
  }

  const percentComplete = Math.round((progress.completedCount / progress.totalSteps) * 100);

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-primary/5 to-transparent" data-testid="onboarding-checklist">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-foreground" />
            <CardTitle className="text-lg" data-testid="text-onboarding-title">Getting Started</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dismissMutation.mutate()}
            disabled={dismissMutation.isPending}
            data-testid="button-dismiss-onboarding"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between gap-2 text-sm mb-1.5">
            <span className="text-muted-foreground">
              {progress.completedCount} of {progress.totalSteps} steps complete
            </span>
            <span className="font-medium">{percentComplete}%</span>
          </div>
          <Progress value={percentComplete} className="h-2" data-testid="progress-onboarding" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {progress.steps.map((step) => {
            const IconComponent = getStepIcon(step.id);
            return (
              <Link key={step.id} href={step.actionUrl} data-testid={`link-onboarding-step-${step.id}`}>
                <div
                  className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                    step.completed
                      ? "opacity-60"
                      : "hover-elevate"
                  }`}
                  data-testid={`onboarding-step-${step.id}`}
                >
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                      step.completed
                        ? "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {step.completed ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <IconComponent className="h-4 w-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className={`text-sm font-medium ${step.completed ? "line-through" : ""}`}>
                        {step.title}
                      </p>
                      {step.optional && (
                        <span className="text-[10px] text-muted-foreground/70 font-normal" data-testid={`text-optional-${step.id}`}>
                          (optional)
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {step.description}
                    </p>
                  </div>
                  {!step.completed && (
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
